import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _createClass from "@babel/runtime/helpers/createClass";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function () { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

import Extent from "../Core/Geographic/Extent";
import GeoJsonParser from "../Parser/GeoJsonParser";
import KMLParser from "../Parser/KMLParser";
import GDFParser from "../Parser/GDFParser";
import GpxParser from "../Parser/GpxParser";
import GTXParser from "../Parser/GTXParser";
import ISGParser from "../Parser/ISGParser";
import VectorTileParser from "../Parser/VectorTileParser";
import Fetcher from "../Provider/Fetcher";
import Cache from "../Core/Scheduler/Cache";
import CRS from "../Core/Geographic/Crs";
export var supportedFetchers = new Map([['image/x-bil;bits=32', Fetcher.textureFloat], ['geojson', Fetcher.json], ['application/json', Fetcher.json], ['application/kml', Fetcher.xml], ['application/gpx', Fetcher.xml], ['application/x-protobuf;type=mapbox-vector', Fetcher.arrayBuffer], ['application/gtx', Fetcher.arrayBuffer], ['application/isg', Fetcher.text], ['application/gdf', Fetcher.text]]);
export var supportedParsers = new Map([['geojson', GeoJsonParser.parse], ['application/json', GeoJsonParser.parse], ['application/kml', KMLParser.parse], ['application/gpx', GpxParser.parse], ['application/x-protobuf;type=mapbox-vector', VectorTileParser.parse], ['application/gtx', GTXParser.parse], ['application/isg', ISGParser.parse], ['application/gdf', GDFParser.parse]]);
var noCache = {
  getByArray: function getByArray() {},
  setByArray: function setByArray(a) {
    return a;
  },
  clear: function clear() {}
};
/**
 * @property {string} crs - data crs projection.
 * @property {boolean} isInverted - This option is to be set to the
 * correct value, true or false (default being false), if the computation of
 * the coordinates needs to be inverted to same scheme as OSM, Google Maps
 * or other system. See [this link]{@link
 * https://alastaira.wordpress.com/2011/07/06/converting-tms-tile-coordinates-to-googlebingosm-tile-coordinates}
 * for more informations.
 *
 */

var InformationsData = /*#__PURE__*/_createClass(function InformationsData(options) {
  _classCallCheck(this, InformationsData);

  /* istanbul ignore next */
  if (options.projection) {
    console.warn('Source projection parameter is deprecated, use crs instead.');
    options.crs = options.crs || options.projection;
  }

  if (options.crs) {
    CRS.isValid(options.crs);
  }

  this.crs = options.crs;
});
/**
 * This class describes parsing options.
 * @property {InformationsData|Source} in - data informations contained in the file.
 * @property {FeatureBuildingOptions|Layer} out - options indicates how the features should be built.
 */
// eslint-disable-next-line


var
/* istanbul ignore next */
ParsingOptions = /*#__PURE__*/_createClass(function ParsingOptions() {
  _classCallCheck(this, ParsingOptions);
});

function fetchSourceData(source, extent) {
  var url = source.urlFromExtent(extent);
  return source.fetcher(url, source.networkOptions).then(function (f) {
    f.extent = extent;
    return f;
  }, function (err) {
    return source.handlingError(err);
  });
}

var uid = 0;
/**
 * @classdesc
 * Sources are object containing informations on how to fetch resources, from a
 * set source.
 *
 * To extend a Source, it is necessary to implement two functions:
 * `urlFromExtent` and `extentInsideLimit`.
 *
 * @property {boolean} isSource - Used to checkout whether this source is a
 * Source. Default is true. You should not change this, as it is used internally
 * for optimisation.
 * @property {number} uid - Unique uid mainly used to store data linked to this
 * source into Cache.
 * @property {string} url - The url of the resources that are fetched.
 * @property {string} format - The format of the resources that are fetched.
 * @property {function} fetcher - The method used to fetch the resources from
 * the source. iTowns provides some methods in {@link Fetcher}, but it can be
 * specified a custom one. This method should return a `Promise` containing the
 * fetched resource. If this property is set, it overrides the chosen fetcher
 * method with `format`.
 * @property {Object} networkOptions - Fetch options (passed directly to
 * `fetch()`), see [the syntax for more information]{@link
 * https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/fetch#Syntax}.
 * By default, set to `{ crossOrigin: 'anonymous' }`.
 * @property {string} crs - The crs projection of the resources.
 * @property {string} attribution - The intellectual property rights for the
 * resources.
 * @property {Extent} extent - The extent of the resources.
 * @property {function} parser - The method used to parse the resources attached
 * to the layer. iTowns provides some parsers, visible in the `Parser/` folder.
 * If the method is custom, it should return a `Promise` containing the parsed
 * resource. If this property is set, it overrides the default selected parser
 * method with `source.format`. If `source.format` is also empty, no parsing
 * action is done.
 * <br><br>
 * When calling this method, two parameters are passed:
 * <ul>
 *  <li>the fetched data, i.e. the data to parse</li>
 *  <li>an {@link ParsingOptions}  containing severals properties, set when this method is
 *  called: it is specific to each call, so the value of each property can vary
 *  depending on the current fetched tile for example</li>
 * </ul>
 */

var Source = /*#__PURE__*/function (_InformationsData) {
  _inherits(Source, _InformationsData);

  var _super = _createSuper(Source);

  /**
   * @param {Object} source - An object that can contain all properties of a
   * Source. Only the `url` property is mandatory.
   *
   * @constructor
   * @extends InformationsData
   */
  function Source(source) {
    var _this;

    _classCallCheck(this, Source);

    _this = _super.call(this, source);
    _this.isSource = true;

    if (!source.url) {
      throw new Error('New Source: url is required');
    }

    _this.uid = uid++;
    _this.url = source.url;
    _this.format = source.format;
    _this.fetcher = source.fetcher || supportedFetchers.get(source.format) || Fetcher.texture;

    _this.parser = source.parser || supportedParsers.get(source.format) || function (d) {
      return d;
    };

    _this.isVectorSource = (source.parser || supportedParsers.get(source.format)) != undefined;
    _this.networkOptions = source.networkOptions || {
      crossOrigin: 'anonymous'
    };
    _this.attribution = source.attribution;
    _this.whenReady = Promise.resolve();
    _this._featuresCaches = {};

    if (source.extent && !source.extent.isExtent) {
      _this.extent = new Extent(_this.crs, source.extent);
    } else {
      _this.extent = source.extent;
    }

    return _this;
  }

  _createClass(Source, [{
    key: "handlingError",
    value: function handlingError(err) {
      throw new Error(err);
    }
    /**
     * Generates an url from an extent. This url is a link to fetch the
     * resources inside the extent.
     *
     * @param {Extent} extent - Extent to convert in url.
      * @return {string} The URL constructed from the extent.
     */
    // eslint-disable-next-line

  }, {
    key: "urlFromExtent",
    value: function urlFromExtent() {
      throw new Error('In extended Source, you have to implement the method urlFromExtent!');
    }
  }, {
    key: "requestToKey",
    value: function requestToKey(extent) {
      return [extent.zoom, extent.row, extent.col];
    }
    /**
     * Load  data from cache or Fetch/Parse data.
     * The loaded data is a Feature or Texture.
     *
     * @param      {Extent}  extent   extent requested parsed data.
     * @param      {FeatureBuildingOptions|Layer}  out     The feature returned options
     * @return     {FeatureCollection|Texture}  The parsed data.
     */

  }, {
    key: "loadData",
    value: function loadData(extent, out) {
      var _this2 = this;

      var cache = this._featuresCaches[out.crs];
      var key = this.requestToKey(extent); // try to get parsed data from cache

      var features = cache.getByArray(key);

      if (!features) {
        // otherwise fetch/parse the data
        features = cache.setByArray(fetchSourceData(this, extent).then(function (file) {
          return _this2.parser(file, {
            out: out,
            "in": _this2
          });
        }, function (err) {
          return _this2.handlingError(err);
        }), key);
        /* istanbul ignore next */

        if (this.onParsedFile) {
          features.then(function (feat) {
            _this2.onParsedFile(feat);

            console.warn('Source.onParsedFile was deprecated');
            return feat;
          });
        }
      }

      return features;
    }
    /**
     * Called when layer added.
     *
     * @param {object} options
     */

  }, {
    key: "onLayerAdded",
    value: function onLayerAdded(options) {
      // Added new cache by crs
      if (!this._featuresCaches[options.out.crs]) {
        // Cache feature only if it's vector data, the feature are cached in source.
        // It's not necessary to cache raster in Source,
        // because it's already cached on layer.
        this._featuresCaches[options.out.crs] = this.isVectorSource ? new Cache() : noCache;
      }
    }
    /**
     * Called when layer removed.
     *
     * @param {options}  [options={}] options
     */

  }, {
    key: "onLayerRemoved",
    value: function onLayerRemoved() {
      var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      // delete unused cache
      var unusedCache = this._featuresCaches[options.unusedCrs];

      if (unusedCache) {
        unusedCache.clear();
        delete this._featuresCaches[options.unusedCrs];
      }
    }
    /**
     * Tests if an extent is inside the source limits.
     *
     * @param {Extent} extent - Extent to test.
      * @return {boolean} True if the extent is inside the limit, false otherwise.
     */
    // eslint-disable-next-line

  }, {
    key: "extentInsideLimit",
    value: function extentInsideLimit() {
      throw new Error('In extented Source, you have to implement the method extentInsideLimit!');
    }
  }]);

  return Source;
}(InformationsData);

export default Source;